import { useEffect, useState, useRef } from 'react';
import { doc, onSnapshot, Timestamp, Unsubscribe } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../config/firebase';
import { message } from 'antd';

interface SyncStatus {
  lastUpdate: Timestamp;
  action: string;
  version: number;
  metadata?: Record<string, any>;
}

export function useFirestoreSync(
  collection: string,
  onUpdate: () => void,
  showNotification = true
) {
  const [status, setStatus] = useState<SyncStatus | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const lastVersionRef = useRef(0);

  useEffect(() => {
    let unsubFirestore: Unsubscribe | null = null;

    // Re-subscribe on every auth state transition: this auto-cleans the
    // previous (possibly stale-auth) subscription on logout/login.
    const unsubAuth = onAuthStateChanged(auth, (fbUser) => {
      if (unsubFirestore) {
        unsubFirestore();
        unsubFirestore = null;
      }
      if (!fbUser) return;

      try {
        unsubFirestore = onSnapshot(
          doc(db, 'sync_status', collection),
          (snapshot) => {
            if (!snapshot.exists()) return;

            const data = snapshot.data() as SyncStatus;
            setStatus(data);

            if (data.version > lastVersionRef.current) {
              lastVersionRef.current = data.version;
              if (showNotification) {
                message.info(`${collection} 資料已更新`);
              }
              onUpdate();
            }
          },
          (err) => {
            console.error('[Firestore Sync] Error:', err);
            setError(err);
            if (err.code !== 'permission-denied' && showNotification) {
              message.error('同步連線錯誤');
            }
          }
        );
      } catch (err) {
        console.error('[Firestore Sync] Setup error:', err);
        setError(err as Error);
      }
    });

    return () => {
      unsubAuth();
      if (unsubFirestore) unsubFirestore();
    };
  }, [collection, onUpdate, showNotification]);

  return { status, error };
}
